import React from 'react';
import { Globe, CheckCircle, ArrowRight, AlertTriangle, Code, Copy } from 'lucide-react';
import ModernDocHeader from '../../../components/documentation/ModernDocHeader';

const WebsiteIntegrationDocs: React.FC = () => {
  const webhookUrl = `${window.location.origin}/api/webhooks/website-form`;
  const exampleApiKey = "your-api-key-here";

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const htmlFormCode = `<!-- Simple Contact Form -->
<form action="${webhookUrl}" method="POST">
  <input type="hidden" name="api_key" value="${exampleApiKey}" />
  <input type="hidden" name="source" value="Website Contact Form" />

  <label>Name:
    <input type="text" name="name" required />
  </label>

  <label>Email:
    <input type="email" name="email" required />
  </label>

  <label>Phone:
    <input type="tel" name="phone" />
  </label>

  <label>Message:
    <textarea name="message"></textarea>
  </label>

  <button type="submit">Submit</button>
</form>`;

  const javascriptCode = `// JavaScript Fetch Example
async function submitForm(formData) {
  try {
    const response = await fetch('${webhookUrl}', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        api_key: '${exampleApiKey}',
        source: 'Website Contact Form',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        // Optional custom fields
        company: formData.company,
        budget: formData.budget,
      })
    });

    const result = await response.json();

    if (result.success) {
      alert('Thank you! We\\'ll be in touch soon.');
    } else {
      alert('Error: ' + result.message);
    }
  } catch (error) {
    console.error('Form submission error:', error);
    alert('Failed to submit form. Please try again.');
  }
}`;

  return (
    <div>
      <ModernDocHeader
        title="Website Forms Integration"
        description="Capture leads directly from your website contact forms, landing pages, and inquiry forms with seamless CRM integration."
        icon={<Globe className="w-6 h-6" />}
        badge="Essential"
        badgeColor="blue"
      />

      <div className="p-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-4xl">
          <div className="prose prose-blue dark:prose-invert max-w-none">

      {/* What You'll Learn */}
      <div className="not-prose bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Code className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-3">
              What You'll Learn
            </h3>
            <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-300">
              <li>✓ Setting up website form webhooks</li>
              <li>✓ HTML form integration methods</li>
              <li>✓ JavaScript API integration</li>
              <li>✓ Custom field mapping</li>
              <li>✓ Form validation and error handling</li>
              <li>✓ Testing and troubleshooting</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Overview</h2>
      <p>
        Website form integration allows you to automatically capture leads from any form on your website
        (contact forms, demo requests, newsletter signups, quote requests) directly into your CRM without
        manual data entry.
      </p>

      <h3>What Gets Captured</h3>
      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              Standard Fields Captured
            </h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
              <li>✓ <strong>Name</strong> - Lead's full name or separate first/last name</li>
              <li>✓ <strong>Email</strong> - Email address (validated format)</li>
              <li>✓ <strong>Phone</strong> - Phone number (with country code)</li>
              <li>✓ <strong>Company</strong> - Company/organization name</li>
              <li>✓ <strong>Message/Inquiry</strong> - Form message or notes</li>
              <li>✓ <strong>Custom Fields</strong> - Any additional fields you define</li>
              <li>✓ <strong>Source</strong> - Identifies which form submitted (e.g., "Contact Form", "Demo Request")</li>
              <li>✓ <strong>IP Address</strong> - Submitter's IP (auto-captured)</li>
              <li>✓ <strong>Timestamp</strong> - When form was submitted</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Prerequisites</h2>
      <ol>
        <li>Access to your website code or content management system (WordPress, etc.)</li>
        <li>Ability to edit HTML/JavaScript or install plugins</li>
        <li>Your CRM API key (found in Settings → API Keys)</li>
      </ol>

      <h2>Integration Methods</h2>

      <h3>Method 1: Direct HTML Form Submission</h3>
      <p>
        <strong>Best for:</strong> Simple contact forms, quick setup, no JavaScript required
      </p>

      <h4>Step 1: Get Your Webhook URL & API Key</h4>
      <div className="not-prose my-6">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-gray-900 dark:text-white">Webhook URL</p>
            <button
              onClick={() => copyToClipboard(webhookUrl)}
              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              title="Copy to clipboard"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <code className="block bg-gray-50 dark:bg-gray-900 p-3 rounded text-sm overflow-x-auto">
            {webhookUrl}
          </code>
        </div>
      </div>

      <p className="text-sm">
        Your API key is available at <code>Settings → API Keys → Generate New Key</code>
      </p>

      <h4>Step 2: Add Form to Your Website</h4>
      <p>Copy and paste this HTML form code into your website:</p>

      <div className="not-prose my-6">
        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
            <p className="font-semibold text-gray-900 dark:text-white text-sm">HTML Form Example</p>
            <button
              onClick={() => copyToClipboard(htmlFormCode)}
              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              title="Copy code"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <pre className="p-4 overflow-x-auto">
            <code className="text-sm text-gray-800 dark:text-gray-200">
              {htmlFormCode}
            </code>
          </pre>
        </div>
      </div>

      <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 my-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-2">
              <strong>Important:</strong>
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              Replace <code>your-api-key-here</code> with your actual API key from CRM settings.
              Do NOT share your API key publicly!
            </p>
          </div>
        </div>
      </div>

      <h3>Method 2: JavaScript API Integration</h3>
      <p>
        <strong>Best for:</strong> Custom forms, single-page applications, advanced validation
      </p>

      <h4>Implementation</h4>
      <div className="not-prose my-6">
        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
            <p className="font-semibold text-gray-900 dark:text-white text-sm">JavaScript Fetch Example</p>
            <button
              onClick={() => copyToClipboard(javascriptCode)}
              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              title="Copy code"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <pre className="p-4 overflow-x-auto">
            <code className="text-sm text-gray-800 dark:text-gray-200">
              {javascriptCode}
            </code>
          </pre>
        </div>
      </div>

      <h3>Method 3: WordPress Plugin</h3>
      <p>
        <strong>Best for:</strong> WordPress sites, no-code solution
      </p>
      <ol>
        <li>Install "Contact Form 7" or "WPForms" plugin</li>
        <li>Create your contact form using the plugin</li>
        <li>Configure webhook integration:
          <ul>
            <li>Install "Webhooks" add-on for your form plugin</li>
            <li>Add webhook URL: <code>{webhookUrl}</code></li>
            <li>Map form fields to CRM fields</li>
            <li>Add API key in custom field</li>
          </ul>
        </li>
        <li>Test form submission</li>
      </ol>

      <h2>Field Mapping</h2>

      <p>Map your form fields to CRM fields using these field names:</p>

      <div className="not-prose">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden my-6">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Form Field Name</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">CRM Field</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Required?</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-3"><code>name</code></td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Lead Name</td>
                <td className="px-4 py-3 text-green-600 dark:text-green-400">Yes</td>
              </tr>
              <tr>
                <td className="px-4 py-3"><code>email</code></td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Email Address</td>
                <td className="px-4 py-3 text-green-600 dark:text-green-400">Yes</td>
              </tr>
              <tr>
                <td className="px-4 py-3"><code>phone</code></td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Phone Number</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">No</td>
              </tr>
              <tr>
                <td className="px-4 py-3"><code>company</code></td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Company Name</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">No</td>
              </tr>
              <tr>
                <td className="px-4 py-3"><code>message</code></td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Lead Remarks</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">No</td>
              </tr>
              <tr>
                <td className="px-4 py-3"><code>source</code></td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Lead Source</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">No</td>
              </tr>
              <tr>
                <td className="px-4 py-3"><code>custom_field_*</code></td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Custom Field</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">No</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>💡 Custom Fields:</strong>
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
          Prefix custom field names with <code>custom_field_</code>:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <li>• <code>custom_field_budget</code> → Budget field in CRM</li>
          <li>• <code>custom_field_industry</code> → Industry field in CRM</li>
          <li>• <code>custom_field_timeline</code> → Timeline field in CRM</li>
        </ul>
      </div>

      <h2>Handling Form Responses</h2>

      <h3>Success Response</h3>
      <p>When form submission succeeds, API returns:</p>
      <div className="not-prose bg-gray-50 dark:bg-gray-900 p-4 rounded my-4">
        <pre className="text-sm text-gray-800 dark:text-gray-200">
{`{
  "success": true,
  "message": "Lead created successfully",
  "lead_id": "12345",
  "status": "new"
}`}
        </pre>
      </div>

      <h3>Error Response</h3>
      <p>If submission fails:</p>
      <div className="not-prose bg-gray-50 dark:bg-gray-900 p-4 rounded my-4">
        <pre className="text-sm text-gray-800 dark:text-gray-200">
{`{
  "success": false,
  "message": "Email field is required",
  "errors": {
    "email": "Invalid email format"
  }
}`}
        </pre>
      </div>

      <h2>Testing Your Integration</h2>

      <h3>Test Checklist</h3>
      <ol>
        <li>Submit test form with valid data
          <ul>
            <li>Check if lead appears in CRM dashboard</li>
            <li>Verify all fields are mapped correctly</li>
            <li>Confirm source is set correctly</li>
          </ul>
        </li>
        <li>Test required field validation
          <ul>
            <li>Submit form without email → Should fail</li>
            <li>Submit form without name → Should fail</li>
            <li>Error messages should display clearly</li>
          </ul>
        </li>
        <li>Test duplicate handling
          <ul>
            <li>Submit same email twice</li>
            <li>Should update existing lead, not create duplicate</li>
          </ul>
        </li>
        <li>Test custom fields
          <ul>
            <li>Verify custom data is captured</li>
            <li>Check field appears in lead details</li>
          </ul>
        </li>
        <li>Check email notifications (if enabled)
          <ul>
            <li>You should receive notification of new lead</li>
            <li>Lead gets auto-response if configured</li>
          </ul>
        </li>
      </ol>

      <div className="not-prose bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 my-6">
        <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
          <strong>💡 Pro Tip:</strong>
        </p>
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Use browser DevTools Network tab to inspect API requests/responses during testing.
          This helps identify any field mapping or validation issues quickly.
        </p>
      </div>

      <h2>Advanced Features</h2>

      <h3>Spam Protection</h3>
      <p>Prevent spam form submissions:</p>
      <ul>
        <li><strong>reCAPTCHA v3</strong> - Add Google reCAPTCHA to your form</li>
        <li><strong>Honeypot Fields</strong> - Hidden fields that bots fill but humans don't</li>
        <li><strong>Rate Limiting</strong> - API blocks excessive submissions from same IP</li>
        <li><strong>Email Verification</strong> - Validate email format and check MX records</li>
      </ul>

      <h3>Thank You Page Redirect</h3>
      <p>Redirect users after successful submission:</p>
      <div className="not-prose bg-gray-50 dark:bg-gray-900 p-4 rounded my-4">
        <pre className="text-sm text-gray-800 dark:text-gray-200">
{`<form action="${webhookUrl}" method="POST">
  <!-- Include redirect URL -->
  <input type="hidden" name="redirect_url"
         value="https://yoursite.com/thank-you" />

  <!-- Other form fields -->
</form>`}
        </pre>
      </div>

      <h3>Auto-Response Emails</h3>
      <p>Send automatic email to lead after form submission:</p>
      <ol>
        <li>Go to <code>Settings → Email Templates</code></li>
        <li>Create "Form Submission" template</li>
        <li>Enable "Send on Form Submission" trigger</li>
        <li>Customize email with merge tags</li>
      </ol>

      <h3>Multi-Step Forms</h3>
      <p>For complex forms with multiple pages:</p>
      <ul>
        <li>Collect data across multiple steps in JavaScript</li>
        <li>Submit all data in final step</li>
        <li>Include step number in form data for tracking</li>
        <li>Use session storage to persist data between steps</li>
      </ul>

      <h2>Troubleshooting</h2>

      <div className="not-prose bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 my-4">
        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Form submits but no lead created?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-2">
            <p><strong>Check:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>API key is correct and not expired</li>
              <li>Webhook URL is exactly correct (no typos)</li>
              <li>Required fields (name, email) are included</li>
              <li>Field names match expected format</li>
              <li>Check API logs in Settings → API Logs for error details</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Getting "Invalid API Key" error?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Solutions:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Regenerate API key in Settings → API Keys</li>
              <li>Update key in your form code (both places if using hidden field)</li>
              <li>Ensure no extra spaces or line breaks in API key</li>
              <li>Check if API key has proper permissions enabled</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            CORS error when submitting via JavaScript?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p>Cross-Origin Resource Sharing (CORS) error means:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Your website domain needs to be whitelisted</li>
              <li>Go to Settings → API Keys</li>
              <li>Add your domain to "Allowed Domains" list</li>
              <li>Format: <code>https://yourwebsite.com</code> (no trailing slash)</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Custom fields not showing in CRM?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Steps to fix:</strong></p>
            <ol className="list-decimal list-inside space-y-1 mt-2">
              <li>Create custom field first in CRM (Settings → Custom Fields)</li>
              <li>Use exact field name in form with <code>custom_field_</code> prefix</li>
              <li>Example: CRM field "Budget" → form field <code>custom_field_budget</code></li>
              <li>Field names are case-sensitive!</li>
            </ol>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Form creates duplicate leads?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p>By default, CRM prevents duplicates by email. If duplicates still occur:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Check if email format varies (different cases, extra spaces)</li>
              <li>Verify duplicate detection is enabled (Settings → Lead Settings)</li>
              <li>Check if API is receiving different email addresses</li>
              <li>Review API logs for multiple submissions</li>
            </ul>
          </div>
        </details>
      </div>

      <h2>Best Practices</h2>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              Form Integration Checklist
            </h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
              <li>✓ <strong>Minimize form fields</strong> - Only ask for essential info (name, email, message)</li>
              <li>✓ <strong>Clear labels</strong> - Make it obvious what each field is for</li>
              <li>✓ <strong>Mobile-friendly</strong> - Test on mobile devices (60%+ traffic is mobile)</li>
              <li>✓ <strong>Fast loading</strong> - Keep form lightweight, no heavy scripts</li>
              <li>✓ <strong>Privacy policy link</strong> - Required by GDPR/data protection laws</li>
              <li>✓ <strong>Error messages</strong> - Show clear, helpful validation errors</li>
              <li>✓ <strong>Success confirmation</strong> - Show thank you message or redirect</li>
              <li>✓ <strong>Test regularly</strong> - Check form monthly to ensure it's working</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Next Steps</h2>

      <div className="not-prose space-y-3 mt-6">
        <a
          href="/docs/drip-campaigns"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Set Up Drip Campaigns</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Auto-nurture leads captured from website forms
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/round-robin"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Round Robin Assignment</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Auto-assign website leads to sales team
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/lead-management"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Manage Your Leads</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Learn how to work with captured leads
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>
      </div>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
              Integration Complete!
            </h3>
            <p className="text-green-800 dark:text-green-300 mb-3">
              Your website is now connected to your CRM. Every form submission will automatically
              create a new lead, ready for your team to follow up.
            </p>
            <a
              href="/settings/api-keys"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
            >
              <Code className="w-4 h-4 mr-2" />
              Manage API Keys
            </a>
          </div>
        </div>
      </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteIntegrationDocs;
